import React, { useState } from 'react';
import { LuKanban, LuLoaderPinwheel, LuPlus, LuTrash2, LuUsers } from 'react-icons/lu';
import { useNavigate } from 'react-router-dom';

import { createStandaloneProject, deleteStandaloneProject, standaloneErrorMessage } from '../../shared/api/standalone';
import { invalidateStandaloneProjectsCache, useStandaloneProjects } from '../../shared/hooks/use-standalone-projects';
import PendingInvitations from '../../widgets/standalone/PendingInvitations';

const StandaloneProjectsPage: React.FC = () => {
  const navigate = useNavigate();
  const { projects, isLoading } = useStandaloneProjects();

  const [isCreating, setIsCreating] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [key, setKey] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const handleCreate = async () => {
    if (!name.trim()) return;
    setIsCreating(true);
    setError(null);
    try {
      const project = await createStandaloneProject(
        name.trim(),
        description.trim() || undefined,
        key.trim() || undefined,
      );
      setShowForm(false);
      setName('');
      setDescription('');
      setKey('');
      navigate(`/standalone/${project._id}`);
    } catch (err) {
      setError(standaloneErrorMessage(err) ?? 'Failed to create project.');
    } finally {
      setIsCreating(false);
    }
  };

  const handleDelete = async (e: React.MouseEvent, projectId: string) => {
    e.stopPropagation();
    if (!window.confirm('Delete this project and all its tasks? This cannot be undone.')) return;
    setDeletingId(projectId);
    try {
      await deleteStandaloneProject(projectId);
      await invalidateStandaloneProjectsCache();
    } catch {
      setError('Failed to delete project.');
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="min-h-screen bg-base-200">
      <div className="p-4 space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-2xl font-semibold flex items-center gap-2">
              <LuKanban className="opacity-70" /> Standalone projects
            </div>
            <div className="text-sm opacity-70">
              Simple task boards mFlow tracks for you — no Jira or Trello needed.
            </div>
          </div>
          <button className="btn btn-primary gap-2" onClick={() => setShowForm((v) => !v)}>
            <LuPlus size={16} /> New project
          </button>
        </div>

        {error && (
          <div className="alert alert-error">
            <span>{error}</span>
          </div>
        )}

        <PendingInvitations />

        {showForm && (
          <div className="card bg-base-100 shadow-card">
            <div className="card-body p-4 gap-3">
              <div className="flex gap-2">
                <input
                  className="input input-bordered flex-1"
                  placeholder="Project name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
                <input
                  className="input input-bordered w-28 font-mono uppercase"
                  placeholder="Key (auto)"
                  maxLength={10}
                  value={key}
                  onChange={(e) => setKey(e.target.value.toUpperCase())}
                  title="Short readable code, e.g. WR — leave blank to auto-generate from the name"
                />
              </div>
              <textarea
                className="textarea textarea-bordered w-full"
                placeholder="Description (optional)"
                rows={2}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
              <div className="flex justify-end gap-2">
                <button className="btn btn-ghost btn-sm" onClick={() => setShowForm(false)} disabled={isCreating}>
                  Cancel
                </button>
                <button className="btn btn-primary btn-sm" onClick={handleCreate} disabled={isCreating || !name.trim()}>
                  {isCreating && <LuLoaderPinwheel className="animate-spin" size={14} />}
                  Create
                </button>
              </div>
            </div>
          </div>
        )}

        {isLoading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="skeleton h-28 w-full rounded-xl" />
            ))}
          </div>
        ) : projects.length === 0 ? (
          <div className="card bg-base-100 shadow-card">
            <div className="card-body p-8 items-center text-center gap-2">
              <LuKanban size={32} className="opacity-30" />
              <div className="text-sm opacity-60">
                No standalone projects yet. Create one, or describe what you&apos;re tracking in chat.
              </div>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
            {projects.map((p) => (
              <div
                key={p._id}
                className="card bg-base-100 shadow-card hover:shadow-md transition-shadow cursor-pointer"
                role="button"
                tabIndex={0}
                onClick={() => navigate(`/standalone/${p._id}`)}
                onKeyDown={(e) => e.key === 'Enter' && navigate(`/standalone/${p._id}`)}
              >
                <div className="card-body p-4 gap-1">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-center gap-1.5 min-w-0">
                      <span className="badge badge-ghost badge-sm font-mono shrink-0">{p.key}</span>
                      <span className="font-semibold truncate">{p.name}</span>
                    </div>
                    <button
                      className="btn btn-ghost btn-xs btn-circle shrink-0"
                      onClick={(e) => handleDelete(e, p._id)}
                      disabled={deletingId === p._id}
                      title="Delete project"
                    >
                      {deletingId === p._id ? (
                        <LuLoaderPinwheel className="animate-spin" size={14} />
                      ) : (
                        <LuTrash2 size={14} />
                      )}
                    </button>
                  </div>
                  {p.description && <div className="text-sm opacity-60 line-clamp-2">{p.description}</div>}
                  <div className="flex items-center gap-2 text-xs opacity-40 mt-1">
                    <span>Created {new Date(p.createdAt).toLocaleDateString()}</span>
                    {(p.members?.filter((m) => m.status === 'accepted').length ?? 0) > 1 && (
                      <span className="badge badge-ghost badge-xs gap-1">
                        <LuUsers size={10} />
                        {p.members?.filter((m) => m.status === 'accepted').length}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default StandaloneProjectsPage;
